import { showError } from './toast';

export const getErrorMessage = (error, defaultMessage = 'Something went wrong') => {
  if (!error) return defaultMessage;
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }
  if (!error.response && !error.status && !error.data) {
    return error.message || 'Unable to connect to the server. Please check your connection and try again.';
  }

  // Prioritize error field from API response (most specific)
  const specificError =
    error.response?.data?.error ||
    error.data?.error ||
    error.response?.data?.data?.error ||
    error.data?.data?.error;

  if (specificError && typeof specificError === 'string') {
    return specificError;
  }

  // Fallback to message fields
  return (
    error.response?.data?.message ||
    error.data?.message ||
    error.message ||
    defaultMessage
  );
};

export const getStatusBasedErrorMessage = (error, customMessages = {}) => {
  // First, try to get the actual error message from API response
  const apiErrorMessage =
    error?.response?.data?.error ||
    error?.data?.error ||
    error?.response?.data?.data?.error ||
    error?.data?.data?.error;

  // If there's a specific error message from API, use it
  if (apiErrorMessage && typeof apiErrorMessage === 'string') {
    return apiErrorMessage;
  }

  const errorStatus = error?.status || error?.response?.status;

  // Use custom messages if provided for this status
  if (customMessages[errorStatus]) {
    return customMessages[errorStatus];
  }

  const statusMessages = {
    400: 'Validation failed. Please check your request.',
    401: 'Authentication failed. Please login again.',
    403: 'Access forbidden. Please check your authentication.',
    404: 'Resource not found. Please check your request.',
    408: 'Request timed out. Please try again.',
    500: 'Server error occurred. Please try again later or contact support if the issue persists.',
    502: 'Bad gateway. Please try again later.',
    503: 'Service unavailable. Please try again later.',
  };

  if (errorStatus && statusMessages[errorStatus]) {
    return getErrorMessage(error, statusMessages[errorStatus]);
  }

  return getErrorMessage(error);
};


export const handleApiError = (error, options = {}) => {
  const {
    customMessages = {},
    showToast = true,
    skipToastForStatus = [401, 403],
  } = options;

  const errorStatus = error?.status || error?.response?.status;
  const errorMessage = getStatusBasedErrorMessage(error, customMessages);

  if (showToast && errorStatus && !skipToastForStatus.includes(errorStatus)) {
    showError(errorMessage);
  }

  const formattedError = error instanceof Error ? error : new Error(errorMessage);
  if (errorStatus) formattedError.status = errorStatus;
  if (error?.data) formattedError.data = error.data;
  if (error?.response?.data) formattedError.data = error.response.data;

  return {
    error: formattedError,
    message: errorMessage,
    status: errorStatus,
  };
};

